import { h, FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import forIn from 'lodash/forIn';
import groupBy from 'lodash/groupBy';
import keys from 'lodash/keys';
import { JSXInternal } from 'preact/src/jsx';
import { Image } from '../types';
import Element = JSXInternal.Element;

const Carousel: FunctionComponent<CarouselProps> = ({ images, selectedColour, onSelect }) => {
    // scroll the carousel to always show the selected colour
    const ulElemRef = useRef<HTMLUListElement>(null);
    useEffect(() => {
        if (ulElemRef.current === null) {
            return;
        }

        const ulElem = ulElemRef.current,
            activeElem = ulElem.querySelector('.active') as HTMLLIElement|null;

        if (activeElem === null) {
            return;
        }

        const ulBounds = ulElem.getBoundingClientRect(),
            activeBounds = activeElem.getBoundingClientRect();

        // don't scroll if the active element is already fully visible, stops click jank
        if ((activeBounds.x >= ulBounds.x) && ((activeBounds.x + activeBounds.width) <= (ulBounds.x + ulBounds.width))) {
            return;
        }

        ulElem.scrollLeft = (activeElem.offsetLeft - ulElem.offsetLeft) - (ulElem.offsetWidth - activeElem.offsetWidth);
    }, [ selectedColour ]);

    // group the available images by colour to create picture elements
    const groupedByColour = groupBy(images, (i: Image) => i.colour);
    const colours: string[] = keys(groupedByColour),
        currentColourIdx = colours.indexOf(selectedColour),
        nextColour = colours[currentColourIdx + 1],
        previousColour = colours[currentColourIdx - 1];
    const liElems: Element[] = [];

    forIn(groupedByColour, (images, colour) => {
        liElems.push(<li key={colour} onClick={() => onSelect(colour)} class={selectedColour === colour ? 'active' : ''} tabIndex={0}>
            <picture class="js-product-image-carousel-thumb">
                {images.map(i => {
                    if (i.mime_type === 'image/jpeg') {
                        return <img key={i.url} src={i.url} alt="" width="42" height="42" />;
                    }

                    return <source key={i.url} srcset={i.url} type={i.mime_type} />;
                })}
            </picture>
        </li>);
    });

    // don't show a carousel with just one image in it
    if (liElems.length === 1) {
        return null;
    }

    return <div class="js-product-image-carousel">
        <button
            type="button"
            onClick={() => onSelect(previousColour)}
            class="js-product-image-carousel-previous"
            disabled={previousColour === undefined}
        >Previous</button>
        <ul ref={ulElemRef}>
            {liElems}
        </ul>
        <button
            type="button"
            onClick={() => onSelect(nextColour)}
            class="js-product-image-carousel-next"
            disabled={nextColour === undefined}
        >Next</button>
    </div>
};

interface CarouselProps {
    images: Image[];
    selectedColour: string;
    onSelect: (colour: string) => void;
}

export default Carousel;
